import {createStore, enableElfProdMode} from '@ngneat/elf'
import {
  deleteEntities,
  resetActiveId,
  selectAllEntities,
  selectAllEntitiesApply,
  setActiveId,
  setEntities,
  updateEntities,
  upsertEntities,
  upsertEntitiesById,
  withActiveId,
  withEntities,
} from '@ngneat/elf-entities'
import {devTools} from '@ngneat/elf-devtools'
import {Room, RoomLastMessage} from '../messaging.types'
import {environment} from '../../../../environments/environment'
import {map} from 'rxjs/operators'
import {Inject, Injectable, PLATFORM_ID} from '@angular/core'
import {isPlatformBrowser} from '@angular/common'

@Injectable({
  providedIn: 'root',
})
export class RoomStore {
  private readonly store = createStore(
    {
      name: 'chat-room-store',
    },
    withEntities<Room>(),
    withActiveId(),
  )

  constructor(@Inject(PLATFORM_ID) private readonly platformId: NonNullable<unknown>) {
    if (isPlatformBrowser(this.platformId)) {
      if (environment.elf.devMode) {
        devTools()
      } else {
        enableElfProdMode()
      }
    }
  }

  hasRoom(roomId: string) {
    return this.store.state.ids.includes(roomId)
  }

  /**
   * Return all chat rooms in the store, order by rooms with most recent message first (no message goes first).
   */
  allRooms$() {
    return this.store.pipe(
      selectAllEntities(),
      map(rooms => [...rooms].sort(sortByLastMessageFirst)),
    )
  }

  /**
   * Return all chat rooms related to the job in the store.
   */
  allRoomsRelatedToJob$(jobId: string) {
    return this.store.pipe(
      selectAllEntitiesApply({
        filterEntity: room => room.relatedInfo?.job?.id === jobId,
      }),
      map(rooms => [...rooms].sort(sortByLastMessageFirst)),
    )
  }

  /**
   * Replace the entire store with the given chat rooms.
   */
  setRooms(rooms: Room[]) {
    this.store.update(setEntities(rooms))
  }

  /**
   * Insert or update the given chat rooms in the store.
   */
  upsertRooms(rooms: Room[]) {
    this.store.update(upsertEntities(rooms))
  }

  /**
   * Insert or update a room in the store, and its last message.
   */
  upsertRoomWithNewLastMessage(roomId: string, lastMessage: RoomLastMessage) {
    const {read} = lastMessage
    this.store.update(
      upsertEntitiesById(roomId, {
        updater: room => ({...room, lastMessage, unreadCount: read ? room.unreadCount : room.unreadCount + 1}),
        creator: id => ({id, lastMessage, unreadCount: read ? 0 : 1}),
      }),
    )
  }

  updateRoomUnreadCount(roomId: string, unreadCount: number) {
    this.store.update(updateEntities(roomId, room => ({...room, unreadCount})))
  }

  /**
   * Remove a room from the store.
   */
  removeRoom(roomId: string) {
    this.store.update(deleteEntities(roomId))
  }

  setActiveRoom(roomId: string) {
    this.store.update(setActiveId(roomId))
  }

  resetActiveRoom() {
    this.store.update(resetActiveId())
  }

  reset() {
    this.store.reset()
  }
}

function sortByLastMessageFirst(
  a: {lastMessage?: {sentAt: string | Date}},
  b: {lastMessage?: {sentAt: string | Date}},
) {
  const aDate = new Date(a.lastMessage?.sentAt || 0)
  const bDate = new Date(b.lastMessage?.sentAt || 0)
  return bDate.getTime() - aDate.getTime()
}
